import { useContext, useEffect, useState } from "react";
import { collection, doc, onSnapshot, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { LanguageContext } from "../context/LanguageContext";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Menu,
  MenuItem,
  ListItemText,
} from "@mui/material";
import MobileHeader from "./headers/MobileHearder";
import { ThemeProvider, createTheme } from "@mui/material/styles";

export default function HelpfaqMobile() {
  const { translate } = useContext(LanguageContext);
  const { currentUser, logOut } = useAuth();
  const [darkMode, setDarkMode] = useState(false);

  const lightTheme = createTheme({
    palette: {
      mode: "light",
      background: { default: "#f4f8f9", paper: "#fff" },
    },
  });
  const darkTheme = createTheme({
    palette: {
      mode: "dark",
      background: { default: "#121212", paper: "#1e1e1e" },
      text: { primary: "#fff", secondary: "#ccc" },
    },
  });

  useEffect(() => {
    if (!currentUser) return;
    const prefRef = doc(db, "profilsUsers", currentUser.uid);
    const unsub = onSnapshot(prefRef, (snap) => {
      if (snap.exists() && snap.data().darkMode !== undefined) {
        setDarkMode(snap.data().darkMode);
      }
    });
    getDoc(prefRef).then((d) => {
      if (d.exists() && d.data().darkMode !== undefined)
        setDarkMode(d.data().darkMode);
    });
    return () => unsub();
  }, [currentUser]);

  return (
    <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
      <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
        <MobileHeader
          title={translate("HelpCenterTitle") || "Help & Resources"}
          showProfileItem={false}
          showGoPremium={false}
        />

        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 4 }}>
          <Container>
            <Typography
              variant="h4"
              fontWeight={700}
              align="center"
              gutterBottom
            >
              {translate("AboutTalkMateTitle") || "What is TalkMate?"}
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center">
              {translate("AboutTalkMateSubtitle") ||
                "TalkMate lets you build, train, and deploy AI agents on all your channels effortlessly."}
            </Typography>
          </Container>

          <Container>
            <Typography
              variant="h5"
              fontWeight={600}
              align="center"
              gutterBottom
            >
              {translate("PricingPlansTitle") || "Our Plans"}
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              {["Free", "Premium"].map((type, i) => (
                <Grid key={i} item xs={12} sm={6}>
                  <Paper
                    sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper" }}
                  >
                    <Typography variant="h6" fontWeight={600}>
                      {type === "Free"
                        ? translate("FreePlanTitle") || "Free Plan"
                        : translate("PremiumPlanTitle") || "Pay-as-you-grow"}
                    </Typography>
                    {type === "Free" ? (
                      <>
                        <Typography>
                          •{" "}
                          {translate("FreePlanFeature1") ||
                            "Test up to 10 questions"}
                        </Typography>
                        <Typography>
                          •{" "}
                          {translate("FreePlanFeature2") ||
                            "Basic access to TalkMate"}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography>
                          •{" "}
                          {translate("PremiumPlanFeature1") ||
                            "Pay per question asked"}
                        </Typography>
                        <Typography>
                          •{" "}
                          {translate("PremiumPlanFeature2") ||
                            "Unlimited agents and analytics"}
                        </Typography>
                        <Typography>
                          •{" "}
                          {translate("PremiumPlanFeature3") ||
                            "Priority email support"}
                        </Typography>
                      </>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>

          <Container>
            <Typography
              variant="h5"
              fontWeight={600}
              align="center"
              gutterBottom
            >
              {translate("WhyChooseTalkMate") || "Why choose TalkMate?"}
            </Typography>
            <Grid container spacing={2}>
              {[
                {
                  title: "Fast setup",
                  desc: "Get your AI assistant running in minutes.",
                },
                {
                  title: "Smart answers",
                  desc: "Leverage GPT-4 for natural and precise responses.",
                },
                {
                  title: "Usage-based pricing",
                  desc: "Only pay when your AI is used.",
                },
              ].map((item, i) => (
                <Grid key={i} item xs={12} sm={4}>
                  <Paper
                    sx={{
                      p: 2,
                      textAlign: "center",
                      borderRadius: 2,
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      gutterBottom
                    >
                      {translate(`WhyChooseFeature${i + 1}Title`) || item.title}
                    </Typography>
                    <Typography color="text.secondary">
                      {translate(`WhyChooseFeature${i + 1}Desc`) || item.desc}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
